/*
  Classique motion: fade + 20px rise as content enters the viewport, once,
  with a short stagger between the pieces of each section. Also staggers the
  cart drawer's lines and summary in when the drawer opens.

  Removable pair: this file + assets/classique-motion.css.
  Skipped entirely in the theme editor, with reduced motion, and without
  IntersectionObserver. Only elements below the fold at load are ever hidden.
*/
(() => {
  const root = document.documentElement;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  window.__classiqueMotionReady = true;

  if (designMode || reducedMotion || !('IntersectionObserver' in window)) {
    root.classList.remove('js');
    return;
  }

  /* The pieces that move: headings, text, buttons, cards. Only the outermost
     match in each section moves, so a card or text block moves as one. */
  const PIECES = [
    '.text-block',
    'h1, h2, h3, h4, h5, h6',
    'main p',
    '.button',
    '.button-secondary',
    '.link',
    '.product-grid__item',
    '.resource-list__item',
    '.group-block--ratio',
    '.media-block',
    '.collection-pills__row',
    '.shop-sidebar',
    'details',
    '.cart-items__table-row',
    '.cart-summary__inner',
    '.cart-items__empty-button',
    '.contact-details__intro',
    '.contact-details__visit',
  ].join(', ');

  /* Never touched: things that already animate or must stay put */
  const SKIP = [
    'header',
    '.shopify-section-group-header-group',
    'sticky-add-to-cart',
    '.whatsapp-float',
    'dialog',
    'media-gallery',
    '.product-media-gallery',
    'slideshow-component',
    'marquee-component',
    '[class*="carousel"]',
    '[class*="jdgm"]',
    '.jdgm-widget',
    '#shopify-product-reviews',
    'shopify-accelerated-checkout',
    'shopify-accelerated-checkout-cart',
    '.shopify-app-block',
    '[data-cs-no-reveal]',
  ].join(', ');

  /* A carousel marked data-cs-reveal-slider (homepage Best Sellers) is the one
     exception to SKIP: its cards (not its arrows and dots) move like the grid's
     did, all together when the slider enters, including cards off to the side. */
  const SLIDER = '[data-cs-reveal-slider]';
  const sliderPieces = new Map();

  /** @param {Element} el */
  const skipped = (el) => {
    const hit = el.closest(SKIP);
    if (!hit) return false;
    const slider = el.closest(SLIDER);
    return !(slider && slider.contains(hit) && el.closest('slideshow-slide'));
  };

  const STAGGER = 0.08;
  const MAX_STEPS = 8;

  /** @param {Element} el */
  const reveal = (el, step) => {
    if (el.getAttribute('data-cs-reveal') !== 'pending') return;
    el.style.setProperty('--cs-reveal-delay', `${Math.min(step, MAX_STEPS) * STAGGER}s`);
    el.setAttribute('data-cs-reveal', 'in');

    const done = () => {
      el.removeAttribute('data-cs-reveal');
      el.style.removeProperty('--cs-reveal-delay');
    };
    el.addEventListener('transitionend', (event) => event.target === el && done(), { once: true });
    setTimeout(done, 1600);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      let step = 0;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        const group = sliderPieces.get(entry.target);
        sliderPieces.delete(entry.target);
        for (const el of group || [entry.target]) reveal(el, step++);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
  );

  const viewportBottom = window.innerHeight;

  // The cart page never animates: its lines, heading and summary must always be
  // visible, even if the observer never fires (seen on iPhone as a blank gap
  // above ORDER SUMMARY). The cart drawer's open stagger below is separate.
  const isCartPage = Boolean(document.querySelector('main cart-items-component'));

  for (const section of isCartPage ? [] : document.querySelectorAll('main .shopify-section')) {
    if (section.closest(SKIP)) continue;
    const found = [...section.querySelectorAll(PIECES)].filter((el) => !skipped(el));
    const pieces = found.filter((el) => !found.some((other) => other !== el && other.contains(el)));

    for (const piece of pieces) {
      // In a slider the whole slide moves: slides clip their content
      const slider = piece.closest(SLIDER);
      const el = slider ? piece.closest('slideshow-slide') : piece;
      const rect = el.getBoundingClientRect();
      // Above the fold (or not laid out): leave it alone
      if (rect.height === 0 || rect.top < viewportBottom) continue;
      el.setAttribute('data-cs-reveal', 'pending');
      if (!slider) {
        observer.observe(el);
        continue;
      }
      if (!sliderPieces.has(slider)) {
        sliderPieces.set(slider, []);
        observer.observe(slider);
      }
      sliderPieces.get(slider).push(el);
    }
  }

  /* Cart drawer: stagger the lines and summary in each time it opens */
  const drawerDialog = document.querySelector('cart-drawer-component dialog');
  if (drawerDialog) {
    new MutationObserver(() => {
      if (!drawerDialog.open) return;
      const items = drawerDialog.querySelectorAll('.cart-drawer__items .cart-items__table-row, .cart-drawer__summary');
      items.forEach((el) => el.setAttribute('data-cs-reveal', 'pending'));
      requestAnimationFrame(() => {
        requestAnimationFrame(() => items.forEach((el, index) => reveal(el, index + 1)));
      });
    }).observe(drawerDialog, { attributes: true, attributeFilter: ['open'] });
  }
})();
